import { Link } from '@tanstack/react-router';
import { Calculator, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { PayoutTable } from '@/components/commission/payout-table';
import { formatCurrency } from '@/lib/utils';

export function CommissionsIndexPage() {
  // Placeholder - will be wired to trpc.commission.myCommission
  const isLoading = false;
  const commission = null as any;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Commissions</h1>
          <p className="text-muted-foreground">Track your earnings and commission tiers.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/commissions/calculator">
            <Button variant="outline" size="sm">
              <Calculator className="mr-2 h-4 w-4" />
              Calculator
            </Button>
          </Link>
          <Link to="/commissions/plans">
            <Button variant="outline" size="sm">
              <Settings className="mr-2 h-4 w-4" />
              Plans
            </Button>
          </Link>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardDescription>Current Commission</CardDescription>
            <CardTitle className="text-3xl">
              {isLoading ? (
                <Skeleton className="h-9 w-32" />
              ) : commission ? (
                formatCurrency(commission.commissionAmount, 'CAD')
              ) : (
                '$0.00'
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">This quarter</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardDescription>Tier Reached</CardDescription>
            <CardTitle className="text-3xl">
              {isLoading ? (
                <Skeleton className="h-9 w-32" />
              ) : commission?.tierReached ? (
                commission.tierReached
              ) : (
                'Base'
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {commission
                ? `${(commission.effectiveRate * 100).toFixed(1)}% effective rate`
                : 'No active plan'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardDescription>Revenue Closed</CardDescription>
            <CardTitle className="text-3xl">
              {isLoading ? (
                <Skeleton className="h-9 w-32" />
              ) : commission ? (
                formatCurrency(commission.totalRevenue, 'CAD')
              ) : (
                '$0.00'
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {commission ? `${commission.closedDeals.length} deals` : '0 deals'}
            </p>
          </CardContent>
        </Card>
      </div>

      {commission?.breakdown && commission.breakdown.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Commission Breakdown</CardTitle>
            <CardDescription>How your commission was calculated this quarter</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {commission.breakdown.map((line: any, i: number) => (
                <div key={i} className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <p className="font-medium">{line.label}</p>
                    <p className="text-sm text-muted-foreground">{line.revenueRange}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{formatCurrency(line.commission, 'CAD')}</p>
                    <p className="text-sm text-muted-foreground">{(line.rate * 100).toFixed(1)}%</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Payout History</CardTitle>
        </CardHeader>
        <CardContent>
          <PayoutTable />
        </CardContent>
      </Card>
    </div>
  );
}
